import * as yup from "yup";
import {OrderDataDTO, PalletType, QuantityOptionDTO, QuoteDTO} from "@/api/data-contracts";
import {convertDate, formatCurrency, formatInteger} from "@/lib/utils";
import {useMemo} from "react";
import {getI18n, useTranslation} from "react-i18next";
import {palletTypeOptions} from "@/datacaches";
import {Constants} from "@/api/constants";
import {RequestI18N} from "@/translations";
import {OrderDataType} from "@/model/orderData";
import {MILLIMETRE_UNIT} from "@/constants";

export type QuoteValidation = Pick<QuoteDTO, "customerReferenceNumber" | "validUntil" | "orderData"> & {
    validUntil?: Date | null;
    orderData?: Pick<
        OrderDataDTO,
        | "deliveryDateFrom"
        | "deliveryDateTo"
        | "glued"
        | "stitched"
        | "taped"
        | "cartonGrade"
        | "palletType"
        | "clicheCosts"
        | "toolingCosts"
        | "palletHeight"
        | "quantityOptions"
        | "paymentTerms"
    > & {
        quantityOptions?: Pick<QuantityOptionDTO, "quantity" | "netPricePer1000">[];
    };
};

/**
 * Collects duplicate quantity validation errors from a list of quantity options.
 * For each quantity value that appears more than once, creates a validation error
 * for each duplicate entry using the provided error factory.
 *
 * @param value - Array of quantity option objects to check for duplicates.
 * @param createError - Factory function that creates a ValidationError for a given index and quantity.
 * @returns Array of ValidationError instances for all duplicate entries.
 */
function collectDuplicateQuantityErrors(
    value: { quantity?: number | null }[],
    createError: (index: number, quantity: number) => yup.ValidationError
): yup.ValidationError[] {
    const duplicates: Record<number, number[]> = {};
    for (const [index, item] of value.entries()) {
        if (item.quantity != null) {
            if (duplicates[item.quantity]) {
                duplicates[item.quantity].push(index);
            } else {
                duplicates[item.quantity] = [index];
            }
        }
    }

    const errors: yup.ValidationError[] = [];
    const nonUniqueQuantities = Object.entries(duplicates).filter(([, indices]) => indices.length > 1);
    for (const [quantity, indices] of nonUniqueQuantities) {
        for (const index of indices) {
            errors.push(createError(index, Number(quantity)));
        }
    }
    return errors;
}

export const useQuoteSchema = (mode: OrderDataType = "request") => {
    const { t } = useTranslation();
    const locale = getI18n().language;
    return useMemo(() => {
        const schema = yup
            .object()
            .shape({
                customerReferenceNumber: yup
                    .string()
                    .optional()
                    .nullable()
                    .max(
                        Constants.CUSTOMER_REFERENCE_NUMBER_LENGTH_MAX,
                        t(RequestI18N.form.validation.quoteNumberMaxLength, {
                            max: formatInteger(Constants.CUSTOMER_REFERENCE_NUMBER_LENGTH_MAX, locale)
                        })
                    ),
                validUntil: yup
                    .date()
                    .optional()
                    .nullable()
                    .transform(convertDate)
                    .typeError(t(RequestI18N.form.validation.dateFormat))
                    .required(t(RequestI18N.form.validation.quoteValidityRequired)),
                additionalInformation: yup
                    .string()
                    .nullable()
                    .optional()
                    .max(
                        Constants.ADDITIONAL_INFORMATION_LENGTH_MAX,
                        t(RequestI18N.form.validation.maxCharactersLength, {
                            max: formatInteger(Constants.ADDITIONAL_INFORMATION_LENGTH_MAX, locale)
                        })
                    ),
                company: yup
                    .object()
                    .nullable()
                    .when([], {
                        is: () => mode === "quote",
                        then: (schema) => schema.required(t(RequestI18N.form.validation.companyRequired))
                    }),
                contactPerson: yup
                    .object()
                    .nullable()
                    .when([], {
                        is: () => mode === "quote",
                        then: (schema) => schema.required(t(RequestI18N.details.field.contactPerson))
                    }),
                orderData: yup.object().shape({
                    deliveryDateFrom: yup
                        .date()
                        .optional()
                        .nullable()
                        .required(t(RequestI18N.form.validation.deliveryDateFrom))
                        .transform(convertDate)
                        .typeError(t(RequestI18N.form.validation.dateFormat)),
                    deliveryDateTo: yup
                        .date()
                        .optional()
                        .nullable()
                        .required(t(RequestI18N.form.validation.deliveryDateTo))
                        .transform(convertDate)
                        .typeError(t(RequestI18N.form.validation.dateFormat)),
                    paymentTerms: yup
                        .string()
                        .nullable()
                        .optional()
                        .max(
                            Constants.PAYMENT_TERMS_LENGTH_MAX,
                            t(RequestI18N.form.validation.maxCharactersLength, {
                                max: formatInteger(Constants.PAYMENT_TERMS_LENGTH_MAX, locale)
                            })
                        ),
                    cartonGrade: yup
                        .object()
                        .shape({
                            code: yup.string().required(t(RequestI18N.form.validation.variety))
                        })
                        .required(t(RequestI18N.form.validation.variety)),
                    palletType: yup.mixed<PalletType>().optional().nullable().oneOf(palletTypeOptions),
                    glued: yup.boolean().nullable().optional(),
                    stitched: yup.boolean().nullable().optional(),
                    taped: yup.boolean().nullable().optional(),
                    palletHeight: yup
                        .number()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .optional()
                        .nullable()
                        .min(
                            Constants.PALLET_HEIGHT_MIN,
                            t(RequestI18N.form.validation.palletHeightMin, {
                                min: formatInteger(Constants.PALLET_HEIGHT_MIN, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        )
                        .max(
                            Constants.PALLET_HEIGHT_MAX,
                            t(RequestI18N.form.validation.palletHeightMax, {
                                max: formatInteger(Constants.PALLET_HEIGHT_MAX, locale),
                                unit: MILLIMETRE_UNIT
                            })
                        )
                        .integer(t(RequestI18N.form.validation.integerOnly)),
                    pricePerPallet: yup
                        .number()
                        .nullable()
                        .optional()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .min(
                            Constants.PRICE_PER_PALLET_MIN,
                            t(RequestI18N.form.validation.pricePerPalletMin, {
                                min: formatInteger(Constants.PRICE_PER_PALLET_MIN, locale),
                                unit: "€"
                            })
                        )
                        .max(
                            Constants.PRICE_PER_PALLET_MAX,
                            t(RequestI18N.form.validation.pricePerPalletMax, {
                                max: formatInteger(Constants.PRICE_PER_PALLET_MAX, locale),
                                unit: "€"
                            })
                        ),
                    quantityPerPallet: yup
                        .number()
                        .nullable()
                        .optional()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .min(
                            Constants.QUANTITY_PER_PALLET_MIN,
                            t(RequestI18N.form.validation.quantityPerPalletMin, {
                                min: formatInteger(Constants.QUANTITY_PER_PALLET_MIN, locale),
                                unit: "Stück"
                            })
                        )
                        .max(
                            Constants.QUANTITY_PER_PALLET_MAX,
                            t(RequestI18N.form.validation.quantityPerPalletMax, {
                                max: formatInteger(Constants.QUANTITY_PER_PALLET_MAX, locale),
                                unit: "Stück"
                            })
                        ),
                    bundlesPerPackage: yup
                        .number()
                        .nullable()
                        .optional()
                        .typeError(t(RequestI18N.form.validation.integerOnly))
                        .min(
                            Constants.BUNDLES_PER_PACKAGE_MIN,
                            t(RequestI18N.form.validation.bundlesPerPackageMin, {
                                min: formatInteger(Constants.BUNDLES_PER_PACKAGE_MIN, locale),
                                unit: "Stück"
                            })
                        )
                        .max(
                            Constants.BUNDLES_PER_PACKAGE_MAX,
                            t(RequestI18N.form.validation.bundlesPerPackageMax, {
                                max: formatInteger(Constants.BUNDLES_PER_PACKAGE_MAX, locale),
                                unit: "Stück"
                            })
                        ),
                    quantityOptions: yup
                        .array(
                            yup
                                .object()
                                .defined()
                                .shape({
                                    quantity: yup
                                        .number()
                                        .typeError(t(RequestI18N.form.validation.integerOnly))
                                        .required(t(RequestI18N.form.validation.quantityRequired))
                                        .min(
                                            Constants.QUANTITY_MIN,
                                            t(RequestI18N.form.validation.quantityMin, {
                                                min: formatInteger(Constants.QUANTITY_MIN, locale)
                                            })
                                        )
                                        .max(
                                            Constants.QUANTITY_MAX,
                                            t(RequestI18N.form.validation.quantityMax, {
                                                max: formatInteger(Constants.QUANTITY_MAX, locale)
                                            })
                                        ),
                                    netPricePer1000: yup
                                        .number()
                                        .nullable()
                                        .when([], {
                                            is: () => mode === "quote",
                                            then: (schema) =>
                                                schema.required(t(RequestI18N.form.validation.netPriceRequired))
                                        })
                                        .test(
                                            "min",
                                            t(RequestI18N.form.validation.netPriceMin, {
                                                minCurrency: formatCurrency(Constants.NET_PRICE_PER_1000_MIN, locale)
                                            }),
                                            (value) => {
                                                if (value === null || value === undefined) {
                                                    return true;
                                                }
                                                return value >= Constants.NET_PRICE_PER_1000_MIN;
                                            }
                                        )
                                        .test(
                                            "max",
                                            t(RequestI18N.form.validation.netPriceMax, {
                                                maxCurrency: formatCurrency(Constants.NET_PRICE_PER_1000_MAX, locale)
                                            }),
                                            (value) => {
                                                if (value === null || value === undefined) {
                                                    return true;
                                                }
                                                return value <= Constants.NET_PRICE_PER_1000_MAX;
                                            }
                                        )
                                })
                        )
                        .defined()
                        .test("uniqueQuantitiesError", function (value) {
                            if (!value) {
                                return true;
                            }
                            const errors = collectDuplicateQuantityErrors(value, (path, quantity) =>
                                this.createError({
                                    path: `${this.path}[${path}].quantity`,
                                    message: t(RequestI18N.form.validation.duplicateQuantity, {quantity})
                                })
                            );
                            if (errors.length > 0) {
                                throw new yup.ValidationError(errors);
                            }
                            return true;
                        }),
                    toolingCosts: yup
                        .number()
                        .optional()
                        .nullable()
                        .when("separateExtraCosts", {
                            is: true,
                            then: (sch) =>
                                sch.when([], {
                                    is: () => mode === "quote",
                                    then: (schema) => schema.required(t(RequestI18N.form.validation.toolingCostsRequired)),
                                    otherwise: (schema) => schema
                                })
                        })
                        .test(
                            "min",
                            t(RequestI18N.form.validation.toolingCostsMin, {
                                minCurrency: formatCurrency(Constants.TOOLING_COSTS_PRICE_MIN, locale)
                            }),
                            (value) => {
                                if (value === null || value === undefined) {
                                    return true;
                                }
                                return value >= Constants.TOOLING_COSTS_PRICE_MIN;
                            }
                        )
                        .test(
                            "max",
                            t(RequestI18N.form.validation.toolingCostsMax, {
                                maxCurrency: formatCurrency(Constants.TOOLING_COSTS_PRICE_MAX, locale)
                            }),
                            (value) => {
                                if (value === null || value === undefined) {
                                    return true;
                                }
                                return value <= Constants.TOOLING_COSTS_PRICE_MAX;
                            }
                        ),

                    clicheCosts: yup
                        .number()
                        .optional()
                        .nullable()
                        .when("separateExtraCosts", {
                            is: true,
                            then: (sch) =>
                                sch.when([], {
                                    is: () => mode === "quote",
                                    then: (schema) => schema.required(t(RequestI18N.form.validation.clicheCostsRequired)),
                                    otherwise: (schema) => schema
                                })
                        })
                        .test(
                            "min",
                            t(RequestI18N.form.validation.clicheCostsMin, {
                                minCurrency: formatCurrency(Constants.CLICHE_COSTS_PRICE_MIN, locale)
                            }),
                            (value) => {
                                if (value === null || value === undefined) {
                                    return true;
                                }
                                return value >= Constants.CLICHE_COSTS_PRICE_MIN;
                            }
                        )
                        .test(
                            "max",
                            t(RequestI18N.form.validation.clicheCostsMax, {
                                maxCurrency: formatCurrency(Constants.CLICHE_COSTS_PRICE_MAX, locale)
                            }),
                            (value) => {
                                if (value === null || value === undefined) {
                                    return true;
                                }
                                return value <= Constants.CLICHE_COSTS_PRICE_MAX;
                            }
                        )
                })
            })
            .stripUnknown();

        return schema as unknown as yup.ObjectSchema<QuoteDTO>;
    }, [locale, t, mode]);
};
